/**
 * Hosting Manager landing — /app/landing/hosting
 *
 * Infrastructure / datacentre / cloud / network — the people who run
 * the substrate. Hero tiles surface cloud providers operational, asset
 * counts by archetype, shadow paths, failing infra adapters, EOL within
 * 90 days, and approved change windows. Two-column body shows network
 * health on the left and lifecycle on the right.
 */
import { useEffect, useState, useCallback } from 'react'
import {
  Server, Cloud, Network, Calendar,
} from 'lucide-react'
import { api } from '../lib/api'
import {
  LandingHeader, HeroTile, TwoColumnBody, Panel, EmptyState,
  StickyFooter, ListRow, safeNum,
} from '../components/landing/LandingShell'

interface CloudStatus { provider: string; status?: string; updated_at?: string; service?: string }
interface ArchCount { archetype: string; count: number }
interface ShadowPath { id: string; src?: string; dst?: string; port?: number; protocol?: string; observed_count?: number }
interface AdapterRow { name: string; status?: string; last_run_at?: string; last_error?: string }
interface EolRow { id: string; product?: string; eol_date?: string; archetype?: string }
interface Proposal { id: string; title?: string; state?: string; window_start?: string }

const INFRA_ADAPTERS = ['f5', 'cisco', 'palo_alto', 'kubernetes', 'aws', 'azure']

export default function HostingLandingPage() {
  const [refreshing, setRefreshing]   = useState(false)
  const [cloud, setCloud]             = useState<CloudStatus[]>([])
  const [archetypes, setArchetypes]   = useState<ArchCount[]>([])
  const [shadow, setShadow]           = useState<ShadowPath[]>([])
  const [adapters, setAdapters]       = useState<AdapterRow[]>([])
  const [eol, setEol]                 = useState<EolRow[]>([])
  const [windows, setWindows]         = useState<Proposal[]>([])

  const load = useCallback(() => {
    setRefreshing(true)
    const j = (p: Promise<any>) => p.catch(() => ({ data: null }))
    Promise.all([
      j(api.get('/api/cloud/status')),
      j(api.get('/api/cdm/overview')),
      j(api.get('/api/cdm/network-paths/shadow', { params: { limit: 10 } })),
      j(api.get('/api/cdm/adapters/health')),
      j(api.get('/api/eol/upcoming', { params: { days: 90 } })),
      j(api.get('/api/cdm/changes/proposals', { params: { state: 'approved', limit: 10 } })),
    ]).then(([c, o, s, h, e, w]) => {
      setCloud(c.data?.providers || c.data?.results || c.data || [])
      const arch = (o.data?.summary?.archetypes || o.data?.archetypes || []) as ArchCount[]
      setArchetypes(arch)
      setShadow(s.data?.paths || s.data?.results || s.data?.flows || [])
      setAdapters(h.data?.adapters || h.data?.results || [])
      setEol(e.data?.upcoming || e.data?.results || e.data?.items || [])
      setWindows(w.data?.proposals || w.data?.results || [])
    }).finally(() => setRefreshing(false))
  }, [])

  useEffect(() => {
    load()
    const id = setInterval(load, 60_000)
    return () => clearInterval(id)
  }, [load])

  const cloudOk = cloud.filter(c => (c.status || '').toLowerCase() === 'operational').length
  const archCount = (name: string) => archetypes.find(a => a.archetype === name)?.count ?? 0
  const serverCount  = archCount('server')
  const cloudVmCount = archCount('cloud_vm')
  const networkCount = archCount('network')
  const infraAdapters = adapters.filter(a => INFRA_ADAPTERS.includes(a.name.toLowerCase()))
  const failingInfra = infraAdapters.filter(a =>
    a.status && !['ok', 'healthy'].includes((a.status || '').toLowerCase()),
  )

  return (
    <div className="space-y-4 animate-fade-in pb-16">
      <LandingHeader
        title="Hosting Landing"
        subtitle="cloud · network · adapters · EOL · change windows"
        persona="hosting"
        onRefresh={load}
        refreshing={refreshing}
      />

      {/* Hero — 6 tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <HeroTile label="Cloud providers OK"
          value={cloud.length === 0 ? '—' : `${cloudOk}/${cloud.length}`}
          tone={cloud.length === 0 ? 'muted'
                : cloudOk === cloud.length ? 'emerald'
                : cloudOk >= cloud.length - 1 ? 'amber' : 'rose'}
          to="/app/cloud" />
        <HeroTile label="Servers / VMs / Net"
          value={`${serverCount}/${cloudVmCount}/${networkCount}`}
          tone="cyan" sub="server·cloud·net" to="/app/cdm" />
        <HeroTile label="Shadow paths" value={safeNum(shadow.length)}
          tone={shadow.length > 0 ? 'amber' : 'emerald'} to="/app/cdm/connectivity" />
        <HeroTile label="Failing infra adapters" value={safeNum(failingInfra.length)}
          tone={failingInfra.length > 0 ? 'rose' : 'emerald'}
          sub={`${INFRA_ADAPTERS.length} watched`} to="/app/cdm/adapters" />
        <HeroTile label="EOL ≤90d" value={safeNum(eol.length)}
          tone={eol.length > 0 ? 'amber' : 'emerald'} to="/app/eol" />
        <HeroTile label="Open change windows" value={safeNum(windows.length)}
          tone={windows.length > 0 ? 'cyan' : 'muted'} to="/app/cdm/changes" />
      </div>

      <TwoColumnBody
        left={(
          <>
            <Panel title="Top shadow paths · observed but not allowed" viewAllTo="/app/cdm/connectivity">
              {shadow.length === 0 ? (
                <EmptyState message="No shadow paths detected."
                  configureTo="/app/cdm/connectivity" configureLabel="open connectivity" />
              ) : (
                <div className="space-y-1">
                  {shadow.slice(0, 5).map(p => (
                    <ListRow key={p.id}
                      primary={`${p.src || '?'} → ${p.dst || '?'}`}
                      secondary={`${p.protocol || 'tcp'}:${p.port ?? '?'}`}
                      right={p.observed_count ? `${p.observed_count.toLocaleString()} flows` : ''} />
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="F5 / Cisco / PAN / K8s / AWS / Azure adapter status" viewAllTo="/app/cdm/adapters">
              {infraAdapters.length === 0 ? (
                <EmptyState message="No infra adapters configured."
                  configureTo="/app/connectors-manage" configureLabel="add an adapter" />
              ) : (
                <div className="space-y-1">
                  {infraAdapters.map(a => {
                    const ok = a.status && ['ok', 'healthy'].includes((a.status || '').toLowerCase())
                    return (
                      <ListRow key={a.name}
                        primary={a.name}
                        secondary={a.last_run_at?.slice(0, 16) || a.last_error}
                        right={<span className={ok ? 'text-emerald' : 'text-rose-light'}>{a.status || 'unknown'}</span>} />
                    )
                  })}
                </div>
              )}
            </Panel>
          </>
        )}
        right={(
          <>
            <Panel title="EOL upcoming · next 90 days" viewAllTo="/app/eol">
              {eol.length === 0 ? (
                <EmptyState message="No EOL items in the next 90 days." />
              ) : (
                <div className="space-y-1">
                  {eol.slice(0, 6).map(e => (
                    <ListRow key={e.id}
                      to="/app/eol"
                      primary={e.product || e.id}
                      secondary={e.archetype}
                      right={e.eol_date} />
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Cloud-status timeline" viewAllTo="/app/cloud">
              {cloud.length === 0 ? (
                <EmptyState message="Cloud-status feed not connected."
                  configureTo="/app/connectors-manage" configureLabel="configure cloud" />
              ) : (
                <div className="space-y-1">
                  {cloud.slice(0, 6).map((c, i) => {
                    const ok = (c.status || '').toLowerCase() === 'operational'
                    return (
                      <ListRow key={`${c.provider}-${i}`}
                        primary={c.provider}
                        secondary={c.service || c.updated_at?.slice(0, 16)}
                        right={<span className={ok ? 'text-emerald' : 'text-amber-light'}>{c.status || 'unknown'}</span>} />
                    )
                  })}
                </div>
              )}
            </Panel>
          </>
        )}
      />

      <StickyFooter actions={[
        { label: 'Connectivity',  icon: Network,  tone: 'primary',   to: '/app/cdm/connectivity' },
        { label: 'Cloud status',  icon: Cloud,    tone: 'secondary', to: '/app/cloud' },
        { label: 'EOL calendar',  icon: Calendar, tone: 'ghost',     to: '/app/eol' },
        { label: 'Adapters',      icon: Server,   tone: 'ghost',     to: '/app/cdm/adapters' },
      ]} />
    </div>
  )
}
